// ===============================
// desiderata_garde_port_royal.jsx
// ===============================
// Saisie des desiderata pour les gardes "Port Royal"
// Rôle :
// - Affiche un tableau par semaines pour un mois configuré
// - Permet à chaque médecin de saisir, pour chaque jour, un niveau de disponibilité
//   (0 = indisponible, 1 = souhaite absolument, 2 = disponible, 3 = peu disponible)
// - Alerte si tous les jours du mois ne sont pas renseignés, sans bloquer la validation
// - Stocke temporairement les choix en localStorage (par médecin et par mois)
// - Envoie les desiderata validés au backend PR2 via POST /api/v2/desiderata-pr2
// - Verrouille la saisie après la date butoir (deadline_iso)
// - ⚙️ Les paramètres du mois sont chargés dynamiquement via GET /api/v2/config/desiderata-pr2
// - ✅ (AJOUT MINIMAL) support multi-mois via cfg.mois_ouverts + sélection du mois
// ===============================

import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "./contexts/AuthContext.jsx";
import LogoutButton from "./components/LogoutButton.jsx";
import { apiFetch, fetchMedecinsMaitre } from "./apiMedecins.js";
import joursFeries from "./joursFeries2025.json";
import { confirmDialog, notify } from "./ui/dialogService.js";
import CenteredModal from "./ui/CenteredModal.jsx";

// ====== Utilitaires ======
const pad2 = (n) => String(n).padStart(2, "0");
const toISO = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const isWeekend = (date) => [0, 6].includes(date.getDay());
const isFerie = (date) =>
  Object.prototype.hasOwnProperty.call(joursFeries, toISO(date));

function isoWeekNumber(date) {
  const tmp = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  tmp.setUTCDate(tmp.getUTCDate() + 4 - ((tmp.getUTCDay() + 6) % 7));
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  return Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
}

function getWeeksForMonth(year, monthIdx) {
  const first = new Date(year, monthIdx, 1);
  const last = new Date(year, monthIdx + 1, 0);
  const start = new Date(first);
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));

  const weeks = [];
  let cursor = new Date(start);
  while (cursor <= last || cursor.getDay() !== 1) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    if (week.some((d) => d.getMonth() === monthIdx)) weeks.push(week);
    if (cursor.getMonth() > monthIdx && cursor.getDay() === 1) break;
  }
  return weeks;
}

// Tous les jours du mois (en ISO) pour la validation complète des desiderata
function getMonthDaysIso(year, monthIdx) {
  const days = [];
  const first = new Date(year, monthIdx, 1);
  const last = new Date(year, monthIdx + 1, 0);
  for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
    days.push(toISO(d));
  }
  return days;
}

// ====== Styles ======
const styles = {
  container: {
    width: "min(calc(100vw - 72px), 1180px)",
    maxWidth: "1180px",
    margin: "clamp(22px, 3dvh, 42px) auto",
    padding: "clamp(22px, 2.2vw, 30px)",
    background: "#f7f9fc",
    borderRadius: "16px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
    fontFamily:
      "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: "16px",
  },
  title: { fontSize: "clamp(28px, 2.15vw, 38px)", fontWeight: 700, margin: 0, color: "#1f2937" },
  subTitle: { fontSize: "clamp(18px, 1.25vw, 22px)", color: "#4b5563", marginTop: "4px" },
  legend: {
    fontSize: "15px",
    color: "#374151",
    marginTop: "8px",
    marginBottom: "12px",
  },
  select: {
    minWidth: "250px",
    padding: "8px 10px",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    background: "#fff",
    fontSize: "15px",
    outline: "none",
  },
  numberInput: {
    width: "72px",
    padding: "6px 8px",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    fontSize: "15px",
  },
  pill: (variant = "neutral") => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 10px",
    borderRadius: "9999px",
    fontSize: "15px",
    fontWeight: 600,
    background:
      variant === "locked"
        ? "rgba(220, 38, 38, 0.10)"
        : "rgba(59, 130, 246, 0.10)",
    color: variant === "locked" ? "#b91c1c" : "#1d4ed8",
    border:
      variant === "locked"
        ? "1px solid rgba(220,38,38,0.25)"
        : "1px solid rgba(59,130,246,0.25)",
    whiteSpace: "nowrap",
  }),
  tableWrap: {
    width: "100%",
    overflowX: "auto",
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    tableLayout: "fixed",
    fontSize: "clamp(14px, 0.95vw, 17px)",
  },
  th: {
    position: "sticky",
    top: 0,
    background: "#f3f4f6",
    color: "#111827",
    fontWeight: 700,
    borderBottom: "1px solid #e5e7eb",
    padding: "8px 6px",
    textAlign: "center",
  },
  td: {
    borderBottom: "1px solid #f1f5f9",
    padding: "4px",
    textAlign: "center",
    verticalAlign: "middle",
  },
  dayCell: (isWe, isFer) => ({
    borderRight: "1px solid #f1f5f9",
    background: isFer
      ? "linear-gradient(180deg, #fef2f2, #fee2e2)" // Rouge clair pour jours fériés
      : isWe
      ? "linear-gradient(180deg, #fef2f2, #fee2e2)" // Rouge clair pour weekends (samedi/dimanche)
      : "#fff",
  }),
  dayHeader: {
    fontSize: "15px",
    fontWeight: 700,
    color: "#374151",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "3px",
  },
  dateBadge: {
    fontSize: "15px",
    color: "#6b7280",
    background: "#eef2ff",
    border: "1px solid #e5e7eb",
    padding: "1px 7px",
    borderRadius: "9999px",
  },
  // 🔴 Badge rouge pour dimanches et jours fériés
  dateBadgeRed: {
    fontSize: "15px",
    color: "#dc2626",
    fontWeight: 700,
    background: "#fee2e2",
    border: "1px solid #fecaca",
    padding: "1px 7px",
    borderRadius: "9999px",
  },
  // 🔴 Nom du jour en rouge pour dimanches et jours fériés
  dayNameRed: {
    color: "#dc2626",
    fontWeight: 700,
  },
  ferBadge: {
    fontSize: "11px",
    color: "#b45309",
    background: "#ffedd5",
    border: "1px solid #fed7aa",
    padding: "2px 6px",
    borderRadius: "9999px",
    marginLeft: "6px",
  },
  selectDispo: {
    width: "100%",
    padding: "5px 7px",
    borderRadius: "7px",
    border: "1px solid #e5e7eb",
    background: "#fff",
    fontSize: "13px",
    outline: "none",
  },
  footer: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: "16px",
  },
  btn: (variant = "primary", disabled = false) => ({
    padding: "10px 14px",
    borderRadius: "10px",
    border: "1px solid transparent",
    fontWeight: 700,
    fontSize: "14px",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
    background: variant === "primary" ? "#111827" : "transparent",
    color: variant === "primary" ? "#fff" : "#111827",
    borderColor: variant === "outline" ? "#d1d5db" : "transparent",
  }),
  note: { fontSize: "12px", color: "#6b7280" },
};

// ====== Options gardes ======
const GARDE_OPTIONS = [
  { code: 0, label: "🚫 Indisponible" },
  { code: 3, label: "😕 Peu disponible" },
  { code: 2, label: "✅ Disponible" },
  { code: 1, label: "⭐ Souhaite absolument" },
];

// ====== Defaults (fallback si config backend indisponible) ======
const DEFAULT_CFG = {
  annee: 2026,
  mois_index: 1, // février
  mois_label: "Février 2026",
  mois_cle: "2026-02",
  deadline_iso: "2026-01-31",
  deadline_affichage: "31/01/2026",
  // ✅ support multi-mois : optionnel
  mois_ouverts: null,
};

// ====== Composant principal ======
const DesiderataGardePortRoyal = ({ gardeType = "PR2" }) => {
  const { user, isAuthenticated } = useAuth();
  const isReanimation = String(gardeType || "").toUpperCase() === "REANIMATION";
  const configEndpoint = isReanimation ? "/api/v2/config/desiderata-reanimation" : "/api/v2/config/desiderata-pr2";
  const medecinsGardesEndpoint = isReanimation ? "/api/v2/medecins-gardes-reanimation" : "/api/v2/medecins-gardes-pr2";
  const desiderataGetBaseEndpoint = isReanimation ? "/api/v2/desiderata-reanimation" : "/api/v2/desiderata-pr2";
  const desiderataPostEndpoint = isReanimation ? "/api/v2/desiderata-reanimation" : "/api/v2/desiderata-pr2";
  const storagePrefix = isReanimation ? "rea-gardes" : "pr2-gardes";
  const sectionLabel = isReanimation ? "Réanimation" : "PR2";
  const [medecins, setMedecins] = useState([]);
  const [values, setValues] = useState({});
  const [nbGardes, setNbGardes] = useState(""); // Vide par défaut pour forcer la saisie
  const [nowIso] = useState(() => toISO(new Date()));

  // Pop-up d'introduction
  const [showIntro, setShowIntro] = useState(true);
  // Pop-up pour désidératas non ouverts
  const [showNotOpen, setShowNotOpen] = useState(false);
  
  // Vérification de participation
  const [isParticipating, setIsParticipating] = useState(null); // null = vérification en cours
  const [showParticipationModal, setShowParticipationModal] = useState(false);
  
  // Médecin connecté (dérivé de user)
  const selectedMedecinKey = useMemo(() => {
    // Si on a user.nom et user.prenom, on peut les utiliser directement
    if (user?.nom && user?.prenom) {
      return `${user.nom}_${user.prenom}`;
    }
    // Sinon, chercher dans la liste des médecins par medecin_id
    if (!user?.medecin_id || !medecins.length) return "";
    const medecin = medecins.find((m) => m.medecin_id === user.medecin_id);
    if (!medecin) return "";
    return `${medecin.nom}_${medecin.prenom}`;
  }, [user, medecins]);

  const normalize = (str) =>
    String(str || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  // ⚙️ Config dynamique (backend)
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [cfgLoading, setCfgLoading] = useState(true);
  const [cfgError, setCfgError] = useState(null);

  // ✅ (AJOUT MINIMAL) mois sélectionné côté médecin (mois_cle)
  const [selectedMoisCle, setSelectedMoisCle] = useState("");

  useEffect(() => {
    const loadCfg = async () => {
      try {
        setCfgLoading(true);
        setCfgError(null);

        const res = await apiFetch(configEndpoint, { cache: "no-store" });
        if (!res.ok) {
          const t = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status} lecture config. ${t}`);
        }

        const json = (await res.json()) || {};
        setCfg((prev) => ({ ...prev, ...json }));
      } catch (e) {
        console.error("❌ Erreur chargement config desiderata :", e);
        setCfgError(e.message || "Erreur inconnue");
        setCfg(DEFAULT_CFG); // fallback
      } finally {
        setCfgLoading(false);
      }
    };

    loadCfg();
  }, [configEndpoint]);

  // ✅ Liste des mois ouverts = exactement celle configurée par le Responsable
  // Si mois_ouverts est un tableau vide => désidératas fermés
  // Le fallback legacy n'est utilisé que si mois_ouverts n'existe pas du tout
  const moisOuverts = useMemo(() => {
    if (Array.isArray(cfg?.mois_ouverts)) {
      // mois_ouverts existe (même vide) => l'utiliser directement
      return cfg.mois_ouverts;
    } else if (cfg?.mois_cle) {
      // mois_ouverts n'existe pas du tout => fallback legacy (ancienne config)
      return [
        {
          annee: cfg.annee,
          mois_index: cfg.mois_index,
          mois_label: cfg.mois_label,
          mois_cle: cfg.mois_cle,
          deadline_iso: cfg.deadline_iso,
          deadline_affichage: cfg.deadline_affichage,
        },
      ];
    }
    return [];
  }, [cfg]);

  // Cache des mois dont la liste a été validée.
  // En saisie désidératas, un mois explicitement rouvert dans la config reste accessible même
  // si une liste validée existe déjà pour ce mois.
  const [moisValides, setMoisValides] = useState({});
  useEffect(() => {
    if (!cfg) return;
    // moisToCheck utilise exactement moisOuverts (même logique)
    let moisToCheck = [];
    if (Array.isArray(cfg?.mois_ouverts)) {
      moisToCheck = [...cfg.mois_ouverts];
    } else if (cfg?.mois_cle) {
      // mois_ouverts n'existe pas du tout => fallback legacy
      moisToCheck = [{ mois_cle: cfg.mois_cle }];
    }
    const listeValideeType = isReanimation ? "REANIMATION" : "PR2";
    const checkMois = async () => {
      const results = {};
      for (const m of moisToCheck) {
        try {
          const q = new URLSearchParams({
            type: listeValideeType,
            for_desiderata_saisie: "1",
          });
          const url = listeValideeType === "PR2"
            ? `/api/v2/listes-gardes-pr2/est-validee/${m.mois_cle}?${q.toString()}`
            : `/api/v2/liste_gardes/est_validee/${m.mois_cle}?${q.toString()}`;
          const res = await apiFetch(url, {
            cache: "no-store",
            credentials: "include",
          });
          if (res.ok) {
            const data = await res.json();
            results[m.mois_cle] = data.estValidee === true;
          }
        } catch {
          results[m.mois_cle] = false;
        }
      }
      setMoisValides(results);
    };
    checkMois();
  }, [cfg, isReanimation]);

  // Si une liste de gardes du mois est déjà validée, ce mois ne doit plus être accessible en saisie côté médecin.
  const moisOuvertsDisponibles = useMemo(() => {
    if (!Array.isArray(moisOuverts)) return [];
    return moisOuverts.filter((m) => {
      const key = String(m?.mois_cle || "");
      if (!key) return false;
      return moisValides[key] !== true;
    });
  }, [moisOuverts, moisValides]);

  // ✅ Initialiser ou resynchroniser le mois sélectionné avec la liste réellement disponible
  useEffect(() => {
    if (moisOuvertsDisponibles.length === 0) return;
    const currentInList = selectedMoisCle && moisOuvertsDisponibles.some((m) => String(m.mois_cle) === String(selectedMoisCle));
    if (currentInList) return;
    const next = cfg?.mois_cle && moisOuvertsDisponibles.some((m) => String(m.mois_cle) === String(cfg.mois_cle))
      ? String(cfg.mois_cle)
      : String(moisOuvertsDisponibles[0].mois_cle);
    setSelectedMoisCle(next);
  }, [cfg, moisOuvertsDisponibles, selectedMoisCle]);

  const selectedMonth = useMemo(() => {
    if (!selectedMoisCle) return null;
    return (
      moisOuvertsDisponibles.find((m) => String(m.mois_cle) === String(selectedMoisCle)) ||
      null
    );
  }, [moisOuvertsDisponibles, selectedMoisCle]);

  // ====== Paramètres (issus du mois sélectionné) ======
  const YEAR = Number(selectedMonth?.annee);
  const MONTH_INDEX = Number(selectedMonth?.mois_index); // JS: 0=Janv, 1=Fév, ...
  const DEADLINE_ISO = String(selectedMonth?.deadline_iso || "");
  const MOIS_LABEL = String(selectedMonth?.mois_label || "");
  const DATE_LIMITE_DESIDERATA = String(selectedMonth?.deadline_affichage || "");
  const MOIS_CLE = String(selectedMonth?.mois_cle || "");

  // 🔍 Vérification de la participation aux gardes
  useEffect(() => {
    const checkParticipation = async () => {
      if (!user?.nom || !user?.prenom) {
        console.log("⏳ [GARDES] En attente des données utilisateur...");
        return; // Attendre que les données soient chargées
      }

      console.log("🔍 [GARDES] Vérification participation pour:", user.nom, user.prenom);

      try {
        const res = await apiFetch(medecinsGardesEndpoint);
        if (res.ok) {
          const medecinsGardes = await res.json();
          console.log("📋 [GARDES] Liste médecins participants:", medecinsGardes.length);
          
          const userNomNorm = normalize(user.nom);
          const userPrenomNorm = normalize(user.prenom);
          
          const isInList = medecinsGardes.some((m) => {
            const mNomNorm = normalize(m.nom);
            const mPrenomNorm = normalize(m.prenom);
            const match = mNomNorm === userNomNorm && mPrenomNorm === userPrenomNorm;
            if (match) {
              console.log("✅ [GARDES] Médecin trouvé dans la liste:", m.nom, m.prenom);
            }
            return match;
          });
          
          console.log("🎯 [GARDES] Résultat vérification:", isInList ? "PARTICIPE" : "NE PARTICIPE PAS");
          setIsParticipating(isInList);
          
          // Ne montrer le modal QUE si on est sûr que le médecin ne participe pas
          if (!isInList) {
            console.log("⚠️ [GARDES] Affichage modal de participation requise");
            setShowParticipationModal(true);
          } else {
            setShowParticipationModal(false);
          }
        } else {
          console.warn("⚠️ [GARDES] Erreur HTTP:", res.status);
          // En cas d'erreur HTTP, on ne bloque pas l'accès (on assume que c'est temporaire)
          setIsParticipating(null); // null = incertain, on ne bloque pas
          setShowParticipationModal(false);
        }
      } catch (err) {
        console.error("❌ [GARDES] Erreur vérification participation:", err);
        // En cas d'erreur, on ne bloque pas l'accès (on assume que c'est temporaire)
        setIsParticipating(null); // null = incertain, on ne bloque pas
        setShowParticipationModal(false);
      }
    };

    checkParticipation();
  }, [medecinsGardesEndpoint, user]);

  // 🔗 Chargement des médecins depuis SQLite API v2
  useEffect(() => {
    fetchMedecinsMaitre()
      .then((data) => setMedecins(data))
      .catch((err) =>
        console.error("❌ Erreur chargement médecins (PR2) :", err)
      );
  }, []);

  const weeks = useMemo(() => {
    if (!Number.isFinite(YEAR) || !Number.isFinite(MONTH_INDEX)) return [];
    return getWeeksForMonth(YEAR, MONTH_INDEX);
  }, [YEAR, MONTH_INDEX]);

  const monthDaysIso = useMemo(() => {
    if (!Number.isFinite(YEAR) || !Number.isFinite(MONTH_INDEX)) return [];
    return getMonthDaysIso(YEAR, MONTH_INDEX);
  }, [YEAR, MONTH_INDEX]);

  const isLocked = useMemo(() => {
    if (!DEADLINE_ISO) return false;
    return new Date(nowIso) > new Date(DEADLINE_ISO);
  }, [nowIso, DEADLINE_ISO]);

  const weekNumbers = useMemo(
    () => weeks.map((w) => isoWeekNumber(w[0])),
    [weeks]
  );

  const storageKey = useMemo(
    () =>
      selectedMedecinKey && MOIS_CLE
        ? `${storagePrefix}:${selectedMedecinKey}:${MOIS_CLE}`
        : null,
    [storagePrefix, selectedMedecinKey, MOIS_CLE]
  );

  const [dataLoaded, setDataLoaded] = useState(false);

  const findServerDesiderataForSelectedMedecin = (serverData) => {
    if (!Array.isArray(serverData) || !selectedMedecinKey) return null;
    const [expectedNom, expectedPrenom] = String(selectedMedecinKey || "").split("_");

    return (
      serverData.find(
        (d) =>
          normalize(d?.nom) === normalize(expectedNom) &&
          normalize(d?.prenom) === normalize(expectedPrenom)
      ) || null
    );
  };

  const applyServerDesiderata = (entry) => {
    if (!entry?.valeurs || Object.keys(entry.valeurs).length === 0) {
      return false;
    }

    setValues(entry.valeurs);
    if (typeof entry.nbGardes === "number" && entry.nbGardes > 0) {
      setNbGardes(entry.nbGardes);
    }
    if (storageKey) {
      localStorage.setItem(
        storageKey,
        JSON.stringify({ nbGardes: entry.nbGardes || 0, souhaits: entry.valeurs })
      );
    }
    return true;
  };

  useEffect(() => {
    if (!storageKey || !MOIS_CLE) return;

    setValues({});
    setNbGardes("");
    setDataLoaded(false);

    const loadDesiderata = async () => {
      let serverReadOk = false;
      try {
        const res = await apiFetch(`${desiderataGetBaseEndpoint}/${MOIS_CLE}`, {
          credentials: "include",
          cache: "no-store",
        });
        if (res.ok) {
          serverReadOk = true;
          const serverData = await res.json();
          if (Array.isArray(serverData) && serverData.length > 0) {
            const myData = findServerDesiderataForSelectedMedecin(serverData);
            if (applyServerDesiderata(myData)) {
              setDataLoaded(true);
              return;
            }
          }
        }
      } catch (err) {
        console.warn("⚠️ Impossible de charger les desiderata PR2 depuis le serveur, fallback localStorage:", err);
      }

      if (serverReadOk) {
        setDataLoaded(true);
        return;
      }

      const raw = localStorage.getItem(storageKey);
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          setValues(parsed.souhaits || {});
          if (typeof parsed.nbGardes === "number" && parsed.nbGardes > 0) {
            setNbGardes(parsed.nbGardes);
          }
        } catch (_) {}
      }
      setDataLoaded(true);
    };

    loadDesiderata();
  }, [storageKey, MOIS_CLE, desiderataGetBaseEndpoint, selectedMedecinKey]);

  useEffect(() => {
    if (storageKey && dataLoaded)
      localStorage.setItem(
        storageKey,
        JSON.stringify({ nbGardes, souhaits: values })
      );
  }, [storageKey, nbGardes, values, dataLoaded]);

  const fmtDay = new Intl.DateTimeFormat("fr-FR", { weekday: "long" });
  const fmtDayNum = new Intl.DateTimeFormat("fr-FR", { day: "2-digit" });

  // Tous les jours du mois doivent avoir un code 0–3
  const hasMissingDays = useMemo(() => {
    if (!selectedMedecinKey) return true;
    return monthDaysIso.some((iso) => {
      const code = values[iso]?.code;
      return !(code === 0 || code === 1 || code === 2 || code === 3);
    });
  }, [monthDaysIso, values, selectedMedecinKey]);

  // --- Gestion des changements de disponibilité ---
  const handleChange = (iso, value) => {
    if (isLocked || !selectedMedecinKey) return;

    const numericCode = parseInt(value, 10);
    const opt = GARDE_OPTIONS.find((o) => o.code === numericCode);

    setValues((prev) => ({
      ...prev,
      [iso]: opt ? { code: numericCode, label: opt.label } : null,
    }));
  };

  // --- Validation (sauvegarde sur le serveur PR2 via /api/v2/desiderata-pr2) ---
  const handleValidate = async () => {
    if (isLocked) {
      notify(
        "Date butoir dépassée : la modification des desiderata n’est plus possible."
      );
      return;
    }

    if (user?.support_readonly) {
      notify(
        "En mode support « lecture seule », l’enregistrement des désidératas est désactivé. Utilisez « Quitter le mode support » ou reconnectez-vous avec votre compte habituel."
      );
      return;
    }

    const erreursBloquantes = [];

    if (!MOIS_CLE) {
      erreursBloquantes.push("• sélectionner un mois (configuration invalide)");
    }

    if (!selectedMedecinKey) {
      erreursBloquantes.push(
        "• votre compte doit être reconnu comme médecin participant (nom en haut de page)"
      );
    }

    if (erreursBloquantes.length > 0) {
      notify(
        [
          "Vous ne pouvez pas encore valider vos desiderata.",
          "",
          "Pour valider, vous devez :",
          "",
          ...erreursBloquantes,
          "",
          "Merci de compléter ces éléments puis de relancer la validation.",
        ].join("\n"),
        {
          presentation: "modal",
          type: "error",
          title: "Validation impossible",
          okLabel: "Compris, fermer",
          durationMs: 0,
        }
      );
      return;
    }

    const nb = Number(nbGardes);
    const nbValide = Number.isInteger(nb) && nb > 0;
    if (!nbValide) {
      notify(
        [
          "Vous ne pouvez pas encore valider vos desiderata.",
          "",
          "Pour valider, vous devez :",
          "",
          "• indiquer le nombre de gardes souhaité pour le mois",
          "",
          "Merci de compléter cet élément puis de relancer la validation.",
        ].join("\n"),
        {
          presentation: "modal",
          type: "error",
          title: "Validation impossible",
          okLabel: "Compris, fermer",
          durationMs: 0,
        }
      );
      return;
    }

    if (hasMissingDays) {
      const continuer = await confirmDialog(
        [
          "Vous n’avez pas renseigné tous vos désidératas.",
          "",
          "Voulez-vous tout de même enregistrer ?",
          "",
          "Vous pourrez compléter ultérieurement.",
        ].join("\n"),
        {
          title: "Jours incomplets",
          okLabel: "J’ai compris, je finaliserai plus tard",
          cancelLabel: "Je poursuis",
        }
      );
      if (!continuer) return;
    }

    const medecin =
      user?.nom && user?.prenom
        ? { nom: user.nom, prenom: user.prenom }
        : (() => {
            const i = selectedMedecinKey.indexOf("_");
            const nom = i >= 0 ? selectedMedecinKey.slice(0, i) : selectedMedecinKey;
            const prenom = i >= 0 ? selectedMedecinKey.slice(i + 1) : "";
            return { nom, prenom };
          })();

    try {
      const response = await apiFetch(desiderataPostEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          mois: MOIS_CLE,
          medecin,
          souhaits: { nbGardes: nb, valeurs: values },
        }),
      });

      if (!response.ok) {
        let detail = "";
        try {
          const ct = response.headers.get("content-type") || "";
          if (ct.includes("application/json")) {
            const j = await response.json();
            detail =
              (j && (j.error || j.message)) ||
              (j && j.ok === false && j.error) ||
              "";
          } else {
            const t = await response.text();
            if (t) detail = t.slice(0, 300);
          }
        } catch (_) {
          /* ignore */
        }
        const msg = detail
          ? `Erreur HTTP ${response.status} : ${detail}`
          : `Erreur HTTP ${response.status}`;
        throw new Error(msg);
      }

      const verifyRes = await apiFetch(`${desiderataGetBaseEndpoint}/${MOIS_CLE}`, {
        credentials: "include",
        cache: "no-store",
      });
      if (!verifyRes.ok) {
        throw new Error(`Sauvegarde non vérifiable (HTTP ${verifyRes.status})`);
      }

      const verifyData = await verifyRes.json();
      const savedEntry = findServerDesiderataForSelectedMedecin(verifyData);
      if (!applyServerDesiderata(savedEntry)) {
        throw new Error("Sauvegarde non retrouvée à la relecture serveur");
      }

      notify(
        [
          "✅ Vos desiderata ont été validés et sauvegardés sur le serveur.",
          "",
          `Vous pouvez les modifier jusqu'à la date limite fixée au ${DATE_LIMITE_DESIDERATA}.`,
        ].join("\n"),
        {
          presentation: "modal",
          type: "success",
          title: "Désidératas enregistrés",
          okLabel: "Compris, fermer",
          durationMs: 0,
        }
      );
    } catch (err) {
      console.error(
        `❌ Erreur sauvegarde désidérata (${isReanimation ? "Réanimation" : "PR2"}) :`,
        err
      );
      notify(
        err?.message && String(err.message).startsWith("Erreur HTTP")
          ? `❌ ${err.message}`
          : "❌ Erreur lors de la sauvegarde sur le serveur. Merci de réessayer ultérieurement."
      );
    }
  };

  // Afficher "désidératas non ouverts" s'il n'y a aucun mois disponible en saisie.
  useEffect(() => {
    if (cfgLoading) {
      setShowNotOpen(false);
      return;
    }
    const shouldShow = moisOuvertsDisponibles.length === 0;
    if (shouldShow) {
      setShowNotOpen(true);
      setShowIntro(false);
    } else {
      setShowNotOpen(false);
    }
  }, [cfgLoading, moisOuvertsDisponibles.length]);

  // ⚠️ Affichage "chargement config" (léger, pour éviter un écran cassé)
  if (cfgLoading) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>Désiderata de garde {sectionLabel}</h1>
        <p style={{ color: "#6b7280" }}>Chargement de la configuration…</p>
      </div>
    );
  }

  // Modal de participation requise - Bloque l'accès si non participant
  // On n'affiche le modal QUE si :
  // 1. showParticipationModal est explicitement true (après vérification)
  // 2. ET isParticipating est false (pas null, pas undefined)
  // 3. ET on a les données utilisateur
  if (showParticipationModal && isParticipating === false && user?.nom && user?.prenom) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #eef2ff 0%, #f9fafb 40%, #ecfeff 100%)",
          padding: "24px",
        }}
      >
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              padding: "32px",
              maxWidth: "500px",
              width: "90%",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚠️</div>
            <h2
              style={{
                fontSize: "24px",
                fontWeight: 700,
                marginBottom: "16px",
                color: "#111827",
              }}
            >
              Participation requise
            </h2>
            <p
              style={{
                fontSize: "16px",
                lineHeight: 1.6,
                color: "#374151",
                marginBottom: "24px",
              }}
            >
              Si vous souhaitez participer à cette activité, merci de cocher la case "Je participe à cette activité" sur la page Espace médecins.
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
              }}
            >
              <button
                type="button"
                onClick={() => window.location.href = "/portail-medecin"}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "1px solid #111827",
                  background: "#111827",
                  color: "#ffffff",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Retour à l'Espace médecins
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Modal pour désidératas non ouverts - Bloque l'accès si les désidératas ne sont pas ouverts
  if (showNotOpen) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #eef2ff 0%, #f9fafb 40%, #ecfeff 100%)",
          padding: "24px",
        }}
      >
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              padding: "32px",
              maxWidth: "500px",
              width: "90%",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>⏳</div>
            <h2
              style={{
                fontSize: "24px",
                fontWeight: 700,
                marginBottom: "16px",
                color: "#111827",
              }}
            >
              Les désidératas ne sont pas encore ouverts
            </h2>
            <p
              style={{
                fontSize: "16px",
                lineHeight: 1.6,
                color: "#374151",
                marginBottom: "24px",
              }}
            >
              Les désidératas de garde ne sont pas encore ouverts pour le moment.
              <br />
              <br />
              Veuillez patienter jusqu'à ce que le responsable ouvre la période de saisie des désidératas.
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
              }}
            >
              <button
                type="button"
                onClick={() => window.location.href = "/portail-medecin"}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "1px solid #111827",
                  background: "#111827",
                  color: "#ffffff",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Retour à l'Espace médecins
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Pop-up d'introduction affichée à l'ouverture de la page Désiderata (seulement si les désidératas sont ouverts) */}
      {showIntro && (
        <CenteredModal labelledBy="desiderata-pr2-intro-title">
	            <h2 id="desiderata-pr2-intro-title" style={{ fontSize: "clamp(20px, 1.35vw, 24px)", fontWeight: 700, marginBottom: 12 }}>
	              ✍️ Comment remplir vos desiderata de garde {sectionLabel}
	            </h2>
	            <ol style={{ paddingLeft: 20, marginBottom: 12, lineHeight: 1.45, fontSize: "clamp(13px, 0.9vw, 15px)" }}>
              <li>
                <strong>1️⃣ Choisissez le mois</strong> (si plusieurs mois sont ouverts).
              </li>
              <li>
                <strong>2️⃣ Indiquez le nombre de gardes</strong> que vous souhaitez
                réaliser pour le mois (champ « Nombre de gardes souhaité »).
              </li>
              <li>
                <strong>3️⃣ Renseignez chaque jour du mois</strong> en choisissant l’une
                des 4 options&nbsp;:
                <br />
                – ⭐ Souhaite absolument<br />
                – ✅ Disponible<br />
                – 😕 Peu disponible<br />
                – 🚫 Indisponible
              </li>
            </ol>
	            <p style={{ fontSize: "clamp(12px, 0.85vw, 14px)", marginBottom: 12, lineHeight: 1.45 }}>
              Une fois tous les jours renseignés et le nombre de gardes indiqué,
              vous pourrez valider vos desiderata pour les transmettre au responsable.
            </p>

            {cfgError ? (
              <p style={{ fontSize: 13, color: "#b91c1c", marginBottom: 12 }}>
                ⚠️ Config non disponible : utilisation d’une config par défaut.
              </p>
            ) : null}

            <div style={{ textAlign: "right" }}>
              <button
                type="button"
                onClick={() => setShowIntro(false)}
	                style={{
	                  padding: "8px 14px",
	                  borderRadius: 8,
	                  border: "1px solid #111827",
	                  background: "#111827",
	                  color: "#ffffff",
	                  fontWeight: 600,
	                  fontSize: "13px",
	                  cursor: "pointer",
	                }}
              >
                Compris
              </button>
            </div>
        </CenteredModal>
      )}

      {/* en-tête */}
      <div style={styles.header}>
        <div style={{ flex: 1 }}>
          <h1 style={styles.title}>Désiderata de garde {sectionLabel} – {MOIS_LABEL}</h1>
          <div style={styles.subTitle}>
            Merci de renseigner vos disponibilités et votre nombre de gardes
            souhaitées.
          </div>
          <div style={styles.legend}>
            💡 Légende : ⭐ Souhaite absolument | ✅ Disponible | 😕 Peu dispo | 🚫 Indisponible
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-end" }}>
          <span style={styles.pill(isLocked ? "locked" : "neutral")}>
            {isLocked
              ? "Verrouillé (date butoir passée)"
              : `Ouvert jusqu'au ${DATE_LIMITE_DESIDERATA}`}
          </span>
          <LogoutButton />
        </div>
      </div>

      {/* ✅ (AJOUT MINIMAL) sélection du mois si plusieurs mois ouverts */}
      {moisOuvertsDisponibles.length > 1 && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
          <select
            value={selectedMoisCle}
            onChange={(e) => {
              setSelectedMoisCle(e.target.value);
              // on garde le médecin sélectionné, mais on rechargera values/nbGardes via storageKey
            }}
            style={styles.select}
          >
            {moisOuvertsDisponibles.map((m) => (
              <option key={String(m.mois_cle)} value={String(m.mois_cle)}>
                {m.mois_label} ({m.mois_cle})
              </option>
            ))}
          </select>
          <span style={{ color: "#6b7280" }}>Mois de saisie</span>
        </div>
      )}

      {/* sélection médecin */}
      <div
        style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        {selectedMedecinKey ? (
          <div
            style={{
              padding: "9px 10px",
              background: "#f0f9ff",
              borderRadius: "8px",
              border: "1px solid #bae6fd",
              flex: 1,
            }}
          >
            <strong style={{ fontSize: "15px", color: "#0369a1" }}>
              👤 Médecin : {selectedMedecinKey.replace("_", " ")}
            </strong>
          </div>
        ) : (
          <div
            style={{
              padding: "9px 10px",
              background: "#fef2f2",
              borderRadius: "8px",
              border: "1px solid #fecaca",
              color: "#991b1b",
              flex: 1,
            }}
          >
            ⚠️ Aucun médecin associé à votre compte. Contactez l'administrateur.
          </div>
        )}

        <input
          type="number"
          min="1"
          max="10"
          step="1"
          value={nbGardes || ""}
          onChange={(e) => {
            const rawVal = e.target.value;
            if (rawVal === "") {
              setNbGardes(""); // Garder vide si l'utilisateur efface
            } else {
              const val = Number(rawVal);
              setNbGardes(isNaN(val) ? "" : val);
            }
          }}
          style={styles.numberInput}
          disabled={isLocked || !selectedMedecinKey}
        />
        <span>gardes souhaitées</span>
      </div>

      {/* tableau par semaines */}
      {weeks.map((week, wIdx) => (
        <div key={wIdx} style={{ marginBottom: 12 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              margin: "6px 6px 8px",
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
              Semaine {weekNumbers[wIdx]}
            </div>
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {week.map((d) => {
                    const iso = toISO(d);
                    const fer = isFerie(d);
                    const isDimanche = d.getDay() === 0;
                    const isSpecial = fer || isDimanche; // Dimanche ou jour férié
                    const dayName =
                      fmtDay.format(d).charAt(0).toUpperCase() +
                      fmtDay.format(d).slice(1);
                    return (
                      <th key={iso} style={styles.th}>
                        <div style={styles.dayHeader}>
                          <span style={isSpecial ? styles.dayNameRed : undefined}>{dayName}</span>
                          <div>
                            <span style={isSpecial ? styles.dateBadgeRed : styles.dateBadge}>
                              {fmtDayNum.format(d)}
                            </span>
                            {fer && (
                              <span style={styles.ferBadge}>
                                {joursFeries[iso] || "Férié"}
                              </span>
                            )}
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {week.map((d) => {
                    const iso = toISO(d);
                    const fer = isFerie(d);
                    const inMonth = d.getMonth() === MONTH_INDEX;
                    return (
                      <td
                        key={iso}
                        style={{
                          ...styles.td,
                          ...styles.dayCell(isWeekend(d), fer),
                        }}
                      >
                        {inMonth ? (
                          <select
                            style={styles.selectDispo}
                            value={values[iso]?.code ?? ""}
                            onChange={(e) => handleChange(iso, e.target.value)}
                            disabled={isLocked || !selectedMedecinKey}
                          >
                            <option value="">— choisir —</option>
                            {GARDE_OPTIONS.map((opt) => (
                              <option key={opt.code} value={opt.code}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span style={{ color: "#9ca3af", fontSize: 12 }}>
                            Hors mois
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* footer */}
      <div style={styles.footer}>
        <button
          type="button"
          onClick={() => (window.location.href = "/portail-medecin")}
          style={styles.btn("primary", false)}
        >
          ← Retour à l&apos;Espace médecins
        </button>

        <button
          type="button"
          onClick={handleValidate}
          style={styles.btn("primary", isLocked)}
          disabled={isLocked}
        >
          Enregistrer mes souhaits
        </button>
      </div>
    </div>
  );
};

export default DesiderataGardePortRoyal;
